import React, { useState, useRef, useContext } from "react";
import Ctx from "../../contexts/AppContext";
import { api } from "../../api";

const TYPES = [
  { id: "broken", label: "Something Broken", icon: "🐛", color: "#ef4444" },
  { id: "suggestion", label: "Suggestion", icon: "💡", color: "#f59e0b" },
  { id: "help", label: "Need Help", icon: "❓", color: "#3b82f6" },
  { id: "review", label: "Good / Review", icon: "⭐", color: "#10b981" },
];

const PAGE_LABELS = {
  // Landlord
  dash: "Dashboard", leads: "Leads", props: "Properties List",
  propDetail: "Property Detail", addProp: "Add Property", editProp: "Edit Property",
  tens: "Tenants List", addTenant: "Add Tenant", editTenant: "Edit Tenant", inviteTenant: "Invite Tenant",
  tenantPayHistory: "Tenant Pay History", pay: "Payments", addPay: "Add Payment",
  paySettings: "Payment Settings", cc: "Common Charges", ccForm: "Edit Common Charges",
  maint: "Maintenance", chatList: "Chat", settings: "Settings",
  agreement: "Agreements", leases: "Agreements", leaseDetail: "Lease Detail",
  leaseForm: "New Lease", editLease: "Edit Lease",
  servicePersons: "Service Persons", addServicePerson: "Add Service Person", editServicePerson: "Edit Service Person",
  mapView: "Map View", team: "Team", reports: "Reports", aiAssistant: "AI Assistant", faq: "FAQ",
  // Tenant
  tenPay: "Pay Now", subMaint: "Submit Maintenance", tenAgr: "My Agreement", tenLease: "My Lease",
  tenChat: "Chat", profile: "Profile",
  // Service Person
  spJobs: "My Jobs", spJobDetail: "Job Detail", spHistory: "Job History", spEarnings: "Earnings",
  // Admin
  adminOrgDetail: "Admin · Org Detail", adminProperties: "Admin · Properties",
  adminTenants: "Admin · Tenants", adminServicePersons: "Admin · Service Persons",
  adminPricing: "Admin · Pricing", adminReports: "Admin · Reports", adminFeedback: "Admin · Feedback",
};

export default function FeedbackButton() {
  const { h, page, role, currUserId } = useContext(Ctx);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("broken");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const longPressTimer = useRef(null);

  const pageLabel = PAGE_LABELS[page] || page || "Unknown";

  const openSheet = () => {
    setNote(""); setType("broken"); setSubmitted(false); setError(""); setOpen(true);
  };

  const handleMouseDown = () => { longPressTimer.current = setTimeout(openSheet, 0); };
  const handleMouseUp = () => { clearTimeout(longPressTimer.current); };

  const handleSubmit = async () => {
    if (!note.trim()) return;
    setLoading(true); setError("");
    try {
      const res = await api.submitFeedback({
        type,
        category: TYPES.find(t => t.id === type)?.label,
        page: pageLabel,
        role: role || "unknown",
        note: note.trim(),
        orgId: localStorage.getItem("orgId") || null,
        userName: localStorage.getItem("currUserId") || null,
      });
      if (res.success) {
        setSubmitted(true);
        setTimeout(() => setOpen(false), 1400);
      } else {
        setError("Failed to save. Try again.");
      }
    } catch {
      setError("Network error. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const selectedType = TYPES.find(t => t.id === type);

  return (
    <>
      {/* Floating FAB */}
      <button
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchEnd={e => { e.preventDefault(); clearTimeout(longPressTimer.current); openSheet(); }}
        title="Feedback / Report Issue"
        style={{
          position: "fixed", bottom: 88, right: 14, zIndex: 200,
          width: 44, height: 44, borderRadius: "50%",
          background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
          border: "none", cursor: "pointer",
          boxShadow: "0 4px 16px rgba(99,102,241,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, userSelect: "none", WebkitUserSelect: "none",
        }}
      >
        🐛
      </button>

      {/* Backdrop */}
      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: "fixed", inset: 0, zIndex: 300,
          background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)",
        }} />
      )}

      {/* Bottom Sheet */}
      {open && (
        <div style={{
          position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
          width: "100%", maxWidth: 430, zIndex: 301,
          background: h.bg, borderRadius: "20px 20px 0 0",
          padding: "16px 16px 36px",
          boxShadow: "0 -8px 32px rgba(0,0,0,0.2)",
        }}>
          {/* Handle */}
          <div style={{ width: 36, height: 4, borderRadius: 2, background: h.bd, margin: "0 auto 14px" }} />

          {submitted ? (
            <div style={{ textAlign: "center", padding: "28px 0" }}>
              <div style={{ fontSize: 44, marginBottom: 8 }}>✅</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: h.tx }}>Report Sent!</div>
              <div style={{ fontSize: 13, color: h.sc, marginTop: 4 }}>Thank you — we'll look into it.</div>
            </div>
          ) : (
            <>
              <div style={{ fontWeight: 700, fontSize: 16, color: h.tx, marginBottom: 2 }}>Send Feedback</div>
              <div style={{ fontSize: 12, color: h.sc, marginBottom: 14 }}>
                Screen: <span style={{ fontWeight: 600, color: h.ac }}>{pageLabel}</span>
              </div>

              {/* Type selector */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                {TYPES.map(tp => (
                  <button key={tp.id} onClick={() => setType(tp.id)} style={{
                    padding: "10px 8px", borderRadius: 12, cursor: "pointer",
                    border: `2px solid ${type === tp.id ? tp.color : h.bd}`,
                    background: type === tp.id ? `${tp.color}14` : "transparent",
                    display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s",
                  }}>
                    <span style={{ fontSize: 18 }}>{tp.icon}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: type === tp.id ? tp.color : h.sc, textAlign: "left" }}>{tp.label}</span>
                  </button>
                ))}
              </div>

              {/* Note */}
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder={
                  type === "broken" ? "What's not working? Describe what happened..." :
                  type === "suggestion" ? "What would you like to see improved?" :
                  type === "help" ? "What do you need help with?" :
                  "What did you like? Share your experience..."
                }
                rows={4}
                autoFocus
                style={{
                  width: "100%", borderRadius: 12, padding: "10px 12px",
                  border: `1.5px solid ${note.trim() ? selectedType?.color : h.bd}`,
                  background: h.cd, color: h.tx, fontSize: 14,
                  resize: "none", outline: "none", fontFamily: "inherit", boxSizing: "border-box",
                  transition: "border-color 0.2s",
                }}
              />

              {error && <div style={{ color: "#ef4444", fontSize: 12, marginTop: 6 }}>{error}</div>}

              <button
                onClick={handleSubmit}
                disabled={!note.trim() || loading}
                style={{
                  marginTop: 12, width: "100%", padding: "13px",
                  borderRadius: 14, border: "none",
                  cursor: note.trim() && !loading ? "pointer" : "not-allowed",
                  background: note.trim() && !loading ? selectedType?.color : h.bd,
                  color: "#fff", fontWeight: 700, fontSize: 15, transition: "all 0.15s",
                }}
              >
                {loading ? "Sending..." : "Send Report"}
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}
